import {getBuildFields} from '../../../rest/builds'
import type {
  BuildId,
  MatrixParameter,
  NormalizedBuild,
  NormalizedMatrixDependency,
  MatrixDependency,
  MatrixDependencyParameter,
  MatrixParameterValue,
} from '../../../types'
import {stringifyId} from '../../../types'
import {BuildState} from '../../common/BuildStateFilter/BuildStateFilter.constants'

import type {ParametersFilter} from './MatrixBuild.types'

export const getMatrixBuildArg = (buildId: BuildId) => ({
  buildLocator: `id:${buildId}`,
  fields: `id,matrixConfiguration(parameters(parameter),dependencies(dependency(build(${getBuildFields(
    {withBuildTypeDetails: true},
  )}),parameters(entry(name,value)))))`,
})

const getParameterValueAlias = (parameterValue?: MatrixParameterValue) => {
  if (parameterValue) {
    const {label, value} = parameterValue

    if (value) {
      return value
    }

    if (label) {
      return label
    }
  }

  return undefined
}

const getBuildKeyByParameters = (parameters: MatrixDependencyParameter[] | undefined = []) =>
  parameters
    .map(({name, value}) => `${name}_${value}`)
    .sort()
    .join('')

export const getIsVisibleBuild = (
  build: NormalizedBuild | null | undefined,
  stateFilter: BuildState,
) =>
  stateFilter === BuildState.ALL ||
  (build?.status === 'FAILURE' && stateFilter === BuildState.FAILED) ||
  (build?.status === 'SUCCESS' && stateFilter === BuildState.SUCCESSFUL) ||
  (build?.state === 'queued' && stateFilter === BuildState.QUEUED) ||
  (build?.canceledInfo != null && stateFilter === BuildState.CANCELED) ||
  (build?.failedToStart === true && stateFilter === BuildState.FAILED_TO_START) ||
  (build?.state === 'running' && stateFilter === BuildState.RUNNING)

function getCorrectBuildStatus(build: NormalizedBuild) {
  const result = new Set<BuildState>()

  if (build?.status === 'FAILURE') {
    result.add(BuildState.FAILED)
  }
  if (build?.status === 'SUCCESS') {
    result.add(BuildState.SUCCESSFUL)
  }
  if (build?.state === 'queued') {
    result.add(BuildState.QUEUED)
  }
  if (build?.canceledInfo != null) {
    result.add(BuildState.CANCELED)
  }
  if (build?.FAILED_TO_START === true) {
    result.add(BuildState.FAILED_TO_START)
  }
  if (build?.state === 'running') {
    result.add(BuildState.RUNNING)
  }

  return result
}

const getHorizontalParameter = (parameters: MatrixParameter[]) =>
  parameters.length > 1 ? parameters[parameters.length - 1] : null
const getVerticalParameters = (parameters: MatrixParameter[]) =>
  parameters.length > 1 ? parameters.slice(0, parameters.length - 1) : parameters

const getDependenciesHash = (dependencies: NormalizedMatrixDependency[]) =>
  dependencies.reduce<Record<string, NormalizedMatrixDependency>>((acc, dependency) => {
    const key = getBuildKeyByParameters(dependency.parameters?.entry)
    if (key !== '' && dependency.build != null) {
      return {...acc, [key]: dependency}
    }
    return acc
  }, {})

const getMatrixHeight = (parameters: MatrixParameter[]) => {
  const verticalParams = getVerticalParameters(parameters)
  return verticalParams.reduce((acc, parameter) => acc * (parameter.value?.length ?? 1), 1)
}

type BuildMatrixColumn = MatrixDependency | null
type BuildMatrixRow = BuildMatrixColumn[]

export const getBuildMatrix = (
  parameters: MatrixParameter[],
  dependencies: NormalizedMatrixDependency[],
  buildsHash: Readonly<Partial<Record<number, NormalizedBuild | null>>> | undefined,
) => {
  const result: BuildMatrixRow[] = []

  const horizontalParameter = getHorizontalParameter(parameters)
  const verticalParameters = getVerticalParameters(parameters)

  const matrixHeight = getMatrixHeight(parameters)
  const matrixWidth = horizontalParameter?.value?.length ?? 1 // 1 - matrix with one parameter

  if (!parameters.length || !dependencies.length || !matrixWidth) {
    return result
  }

  const temp: number[] = new Array(verticalParameters.length).fill(0)
  // "temp" helps to make the structure as shown below
  //
  // Chrome  20  true
  //             false
  //         30  true
  //             false
  //
  const dependenciesHash = getDependenciesHash(dependencies)

  for (let i = 0; i < matrixHeight; i++) {
    const columns: (MatrixDependency | null)[] = []
    const rowParameters: MatrixDependencyParameter[] = []

    for (let j = 0; j < temp.length; j++) {
      const parameterValue = verticalParameters[j].value?.[temp[j]]
      const value = getParameterValueAlias(parameterValue)

      rowParameters[j] = {
        name: verticalParameters[j].name,
        value,
      }

      if (j === temp.length - 1) {
        temp[j] += 1
      }
    }
    for (let j = temp.length - 1; j >= 0; j--) {
      const valuesCount = verticalParameters[j]?.value?.length
      if (valuesCount != null && temp[j] > valuesCount - 1 && j !== 0) {
        temp[j] = 0
        temp[j - 1] += 1
      }
    }

    for (let j = 0; j < matrixWidth; j++) {
      const parameterValue = horizontalParameter?.value?.[j]
      const value = getParameterValueAlias(parameterValue)
      const columnParamete = {
        name: horizontalParameter?.name,
        value,
      }
      const key = getBuildKeyByParameters(
        horizontalParameter != null ? [...rowParameters, columnParamete] : rowParameters,
      )
      const normalizedDependency = dependenciesHash[key]
      const dependencyId = normalizedDependency?.build

      if (dependencyId != null) {
        const build = buildsHash?.[dependencyId]
        if (build != null) {
          columns.push({
            ...normalizedDependency,
            build,
          })
          continue
        }
      }
      columns.push(null)
    }
    result.push(columns)
  }
  return result
}

export const filterBuildMatrix = (
  matrix: BuildMatrixRow[],
  stateFilter: BuildState,
  parametersFilter: ParametersFilter | null,
) => {
  const result: BuildMatrixRow[] = []

  const notEmptyRows: Record<number, boolean> = {}
  const notEmptyColumns: Record<number, boolean> = {}

  // Filters by build status and parameters
  for (let i = 0; i < matrix.length; i++) {
    const columns = matrix[i]
    const row: BuildMatrixRow = []

    for (let j = 0; j < matrix[i].length; j++) {
      let isVisible = false
      const column = columns[j]

      if (getIsVisibleBuild(column?.build, stateFilter) && column?.parameters?.entry != null) {
        isVisible = column.parameters.entry.every(
          ({name, value}) =>
            name != null && value != null && (parametersFilter?.[name]?.includes(value) ?? true),
        )
      }
      if (!notEmptyColumns[j]) {
        notEmptyColumns[j] = isVisible
      }
      row.push(isVisible ? column : null)
    }
    notEmptyRows[i] = row.filter(Boolean).length > 0
    result.push(row)
  }

  // Filters empty rows and columns
  const filteredResult: BuildMatrixRow[] = []
  for (let i = 0; i < result.length; i++) {
    if (notEmptyRows[i]) {
      const row: BuildMatrixRow = []
      for (let j = 0; j < result[i].length; j++) {
        if (notEmptyColumns[j]) {
          row.push(result[i][j])
        }
      }
      filteredResult.push(row)
    }
  }

  return filteredResult
}

export enum MatrixColumnType {
  PARAMETER_NAME = 'parameterName',
  PARAMETER_VALUE = 'parameterValue',
  BUILD = 'build',
}

export type MatrixColumn =
  | {
      id: string
      type: MatrixColumnType.PARAMETER_NAME | MatrixColumnType.PARAMETER_VALUE
      value: string | undefined | null
      label?: string | null
      isVisible: boolean
    }
  | {
      id: string
      type: MatrixColumnType.BUILD
      value: NormalizedBuild | undefined | null
      isVisible: boolean
    }

export type MatrixRow = {
  id: string
  columns: MatrixColumn[]
}

export const getFullMatrix = (buildMatrix: BuildMatrixRow[], parameters: MatrixParameter[]) => {
  const result: MatrixRow[] = []
  const currentStatuses = new Set<string>()
  const parametersOrder = new Map<string, number>()
  const parametersValueMap = parameters
    .flatMap<MatrixParameterValue & {name?: string}>(
      ({name, value = []}) => value?.map(item => ({...item, name})),
    )
    .reduce<Map<string | undefined, string>>((accumulator, parameter, index) => {
      if (parameter != null) {
        const {name = '', label, value} = parameter
        parametersOrder.set(name, index)
        accumulator.set(value, label || value)
      }

      return accumulator
    }, new Map())
  const horizontalParameter = getHorizontalParameter(parameters)
  const verticalParameters = getVerticalParameters(parameters)

  if (buildMatrix.length === 0) {
    return {currentStatuses, matrix: result}
  }

  // MATRIX BODY
  for (let i = 0; i < buildMatrix.length; i++) {
    const matrixColumns: MatrixColumn[] = []

    for (let j = 0; j < buildMatrix[i].length; j++) {
      const buildMatrixColumn = buildMatrix[i][j]

      if (buildMatrixColumn != null) {
        buildMatrixColumn.parameters?.entry
          ?.filter(({name}) => horizontalParameter?.name !== name)
          ?.sort((current, previous) => {
            const {name: currentName = ''} = current
            const {name: previousName = ''} = previous
            const currentPosition = parametersOrder.get(currentName) ?? 0
            const previousPosition = parametersOrder.get(previousName) ?? 0

            return currentPosition - previousPosition
          })
          ?.forEach((parameter, parameterIndex) => {
            const {name, value} = parameter
            const label = parametersValueMap.get(value)

            matrixColumns.push({
              id: `${name}-${value}-${buildMatrixColumn.build?.id}`,
              type: MatrixColumnType.PARAMETER_VALUE,
              isVisible:
                matrixColumns?.[parameterIndex - 1]?.isVisible ||
                result?.[i - 1]?.columns?.[parameterIndex]?.value !== value,
              label,
              value,
            })
          })
        break
      }
    }

    for (let j = 0; j < buildMatrix[i].length; j++) {
      const buildMatrixColumn = buildMatrix[i][j]
      const {build} = buildMatrixColumn ?? {}
      const correctBuildStatuses = build && getCorrectBuildStatus(build)

      if (correctBuildStatuses?.size) {
        correctBuildStatuses.forEach(status => status && currentStatuses.add(status))
      }

      matrixColumns.push({
        id: stringifyId(build?.id),
        type: MatrixColumnType.BUILD,
        isVisible: build != null,
        value: build,
      })
    }

    result.push({
      id: matrixColumns.map(({id}) => id).join('-'),
      columns: matrixColumns,
    })
  }

  // MATRIX HEADER
  const matrixHeaderColumns: MatrixColumn[] = []
  for (let i = 0; i < verticalParameters.length; i++) {
    const verticalName = verticalParameters[i]?.name
    const horizontalName = horizontalParameter?.name

    let value = verticalName ?? ''
    if (i === verticalParameters.length - 1 && verticalName != null && horizontalName != null) {
      value = `${verticalName} / ${horizontalName}`
    }

    matrixHeaderColumns.push({
      id: value,
      type: MatrixColumnType.PARAMETER_NAME,
      isVisible: true,
      value,
    })
  }

  if (horizontalParameter == null) {
    matrixHeaderColumns.push({
      id: `EMPTY`,
      type: MatrixColumnType.PARAMETER_VALUE,
      isVisible: false,
      value: '',
    })
  } else {
    for (let i = 0; i < buildMatrix[0].length; i++) {
      let dependency: BuildMatrixColumn = null
      for (let j = 0; j < buildMatrix.length; j++) {
        if (buildMatrix[j][i] != null) {
          dependency = buildMatrix[j][i]
          break
        }
      }
      const parameter = dependency?.parameters?.entry?.find(
        ({name}) => horizontalParameter?.name === name,
      )
      const value = parameter?.value
      const label = parametersValueMap.get(value)

      matrixHeaderColumns.push({
        id: value ?? '',
        type: MatrixColumnType.PARAMETER_VALUE,
        isVisible: true,
        value,
        label,
      })
    }
  }

  result.unshift({
    id: matrixHeaderColumns.map(({id}) => id).join('-'),
    columns: matrixHeaderColumns,
  })

  return {
    currentStatuses,
    matrix: result,
  }
}
